import { useCallback } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EmberRoute } from '../../../enums/emberRoute'
import { useEmberRouteToUrlGenerator } from '../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useOrganizationSource } from '../../../hooks/useOrganizationSource'
import { getOrganizationViewUrl } from '../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { TransactionOriginatorType } from '../enums/transactionOriginatorType'

interface GetOriginatorUrlParams {
  externalId?: string
  originatorType?: TransactionOriginatorType
  originatorId?: string
  transactionId?: string
}

const emberRouteMap: Partial<Record<TransactionOriginatorType, EmberRoute>> = {
  [TransactionOriginatorType.BankPayment]: EmberRoute.BankPayment,
  [TransactionOriginatorType.DaybookTransaction]: EmberRoute.DaybookTransaction,
  [TransactionOriginatorType.ContactBalancePayment]: EmberRoute.ContactBalancePayment,
  /* Intended change: ConversionBalance route doesn't exist anymore and behaviour is very legacy. */
  /* It has not been possible to make this type of transaction for the past 3 years (officially). */
  /* Even before, it was broken and rarely used. */
  /* In rare instances it could be visible, there won't be and shouldn't be any links from this util. Components handle the rest */
  [TransactionOriginatorType.ConversionBalance]: undefined,
  [TransactionOriginatorType.ExternalInvoice]: EmberRoute.Invoice,
  [TransactionOriginatorType.Invoice]: EmberRoute.Invoice,
  [TransactionOriginatorType.InvoiceLateFee]: EmberRoute.InvoiceLateFee,
}

export const useBuildTransactionUrl = () => {
  const { organization } = useUserOrganization()
  const { isSourceZervant } = useOrganizationSource()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const organizationUrl = organization?.url

  const getUrl = useCallback(
    ({ externalId, originatorType, originatorId }: GetOriginatorUrlParams): string | undefined => {
      if (!originatorType || !originatorId) {
        return undefined
      }

      const baseUrl = `${window.location.origin}`

      /* React routes */
      if (originatorType === TransactionOriginatorType.Bill) {
        return `${baseUrl}${getOrganizationViewUrl(
          organizationUrl,
          reactRoute.organization.bills.getViewBillRoute(originatorId),
        )}`
      }

      const isInvoiceType =
        originatorType === TransactionOriginatorType.Invoice ||
        originatorType === TransactionOriginatorType.InvoiceLateFee ||
        originatorType === TransactionOriginatorType.ExternalInvoice

      /* Special invoice routing for Zervant-based invoices, reactified part */
      if (isInvoiceType && isSourceZervant && externalId) {
        const invoiceDocumentType = 'invoice'
        return `${baseUrl}${getOrganizationViewUrl(
          organizationUrl,
          reactRoute.organization.invoices.getViewInvoiceRoute(externalId, invoiceDocumentType),
        )}`
      }

      if (
        originatorType === TransactionOriginatorType.SalesTaxReturn ||
        originatorType === TransactionOriginatorType.SalesTaxPayment
      ) {
        return `${baseUrl}${getOrganizationViewUrl(
          organizationUrl,
          reactRoute.organization.vatDeclarations.getViewVatDeclarationRoute(originatorId),
        )}`
      }

      if (!generateUrl) {
        return undefined
      }

      const emberRoute = emberRouteMap[originatorType]
      if (emberRoute) {
        return generateUrl(emberRoute, originatorId)
      }

      return undefined
    },
    [organizationUrl, generateUrl, isSourceZervant],
  )

  return { getUrl }
}
